import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Drama } from '@/mocks/dramas';

interface HeroSliderProps {
  items: Drama[];
}

export default function HeroSlider({ items }: HeroSliderProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      setActive(((next % items.length) + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  const current = items[active];

  return (
    <section
      className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) go(active + (delta < 0 ? 1 : -1));
        touchStartX.current = null;
      }}
      aria-label="焦点剧集幻灯片"
    >
      {items.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            idx === active ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden={idx !== active}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/92 via-primary-950/70 to-primary-950/25"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-transparent to-primary-950/35"></div>

      <div className="relative z-10 w-full h-full px-4 md:px-10 flex items-end md:items-center pb-24 md:pb-0">
        <div className="max-w-[640px]">
          <div key={current.id} className="animate-fade-up">
            <div className="flex flex-wrap items-center gap-2 mb-4 md:mb-5">
              <span className="h-6 px-2.5 rounded-md bg-accent-500 text-primary-950 text-[11px] font-medium flex items-center gap-1">
                <i className="ri-fire-line text-xs"></i>
                本站精选
              </span>
              {current.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="h-6 px-2.5 rounded-md border border-background-50/30 text-background-100 text-[11px] flex items-center"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h2 className="font-heading text-3xl md:text-[52px] leading-[1.15] font-semibold text-background-50 mb-3 md:mb-4 tracking-tight">
              {current.title}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs md:text-sm text-background-200 mb-4">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-400"></i>
                <span className="text-accent-300 font-medium">{current.rating.toFixed(1)}</span>
              </span>
              <span className="w-px h-3 bg-background-200/40"></span>
              <span>{current.year}</span>
              <span className="w-px h-3 bg-background-200/40"></span>
              <span>{current.region}</span>
              <span className="w-px h-3 bg-background-200/40"></span>
              <span>导演 {current.director}</span>
              <span className="w-px h-3 bg-background-200/40"></span>
              <span>{current.status}</span>
            </div>
            <p className="text-sm md:text-base leading-7 md:leading-8 text-background-200 line-clamp-3 mb-6 md:mb-8 max-w-xl">
              {current.summary}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="h-11 md:h-12 px-6 md:px-7 rounded-md bg-accent-500 text-primary-950 text-sm font-medium flex items-center gap-2 whitespace-nowrap hover:bg-accent-400 transition-colors"
              >
                <i className="ri-play-circle-line text-lg"></i>
                查看剧集详情
              </Link>
              <Link
                to="/category/latest"
                className="h-11 md:h-12 px-6 md:px-7 rounded-md border border-background-50/40 text-background-50 text-sm flex items-center gap-2 whitespace-nowrap hover:bg-background-50/10 transition-colors"
              >
                <i className="ri-list-check-2"></i>
                浏览全部新剧
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute z-10 right-4 md:right-10 bottom-6 md:bottom-10 flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          {items.map((item, idx) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${idx + 1} 部：${item.title}`}
              onClick={() => go(idx)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                idx === active ? 'w-8 bg-accent-500' : 'w-4 bg-background-50/40 hover:bg-background-50/70'
              }`}
            ></button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label="上一部"
            onClick={() => go(active - 1)}
            className="w-9 h-9 rounded-full border border-background-50/30 text-background-100 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            type="button"
            aria-label="下一部"
            onClick={() => go(active + 1)}
            className="w-9 h-9 rounded-full border border-background-50/30 text-background-100 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>

      <div className="absolute z-10 right-4 md:right-10 top-6 md:top-auto md:bottom-28 lg:bottom-32 hidden md:flex flex-col items-end gap-1.5">
        <span className="font-heading text-5xl lg:text-6xl text-background-50/25 leading-none">
          {String(active + 1).padStart(2, '0')}
        </span>
        <span className="text-[11px] tracking-[0.2em] text-background-200/60">
          / {String(items.length).padStart(2, '0')}
        </span>
      </div>
    </section>
  );
}